import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MoneyInput } from '../components/MoneyInput';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BookOpen, FileSpreadsheet, FileText, Plus, Trash2, RefreshCw, AlertCircle, Search, X, Link2 } from 'lucide-react';
import { SearchableSelect } from '../components/SearchableSelect';
import { JournalEntryAuditDialog, EntryAuditButton } from '../components/JournalEntryAuditDialog';
import { toast } from 'sonner';
import { fmtDateTime, todayLocalISO } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function userCan(user, mod, act) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[mod]?.[act] === true;
}

const SOURCE_LABELS = {
  resident_invoice: 'FC Residente', insurer_invoice: 'FC Obra Social', credit_note: 'Nota de Crédito',
  resident_collection: 'Cobro Residente', insurer_receipt: 'Recibo OS', treasury_collection: 'Cobranza',
  treasury_payment: 'Orden de Pago', treasury_adjustment: 'Ajuste', treasury_transfer: 'Transferencia',
  treasury_advance: 'Anticipo a rendir', treasury_advance_settlement: 'Rendición de anticipo',
  advance_settlement: 'Rendición de anticipo',
  purchase_invoice: 'FC Compra',
  deferred_check_paid: 'Cheque diferido pagado',
  deferred_check_rejected: 'Cheque diferido rechazado',
  reconciliation_adjustment: 'Ajuste de Conciliación',  // v3.40.3
  manual: 'Manual',
};

// Traducción de las cuentas/mapeos faltantes a lenguaje entendible
const SYSTEM_LABELS = {
  system_iva_debito: 'IVA Débito Fiscal (a pagar)',
  system_iva_credito: 'IVA Crédito Fiscal (a favor)',
  system_deudores_residentes: 'Deudores - Residentes',
  system_deudores_os: 'Deudores - Obras Sociales',
  system_proveedores: 'Proveedores a pagar',
  system_valores_en_cartera: 'Valores en cartera',
  system_anticipos_residentes: 'Anticipos de residentes',
  system_anticipos_a_rendir: 'Anticipos a rendir (adelantos)',
};

const missingLabel = (m) => {
  if (!m) return '';
  if (SYSTEM_LABELS[m]) return SYSTEM_LABELS[m];
  if (m.startsWith('expense_concept')) return 'Concepto de gasto sin cuenta asignada';
  if (m.startsWith('income_concept')) return 'Concepto de ingreso sin cuenta asignada';
  if (m.startsWith('cash_bank')) return 'Caja/Banco sin cuenta asignada';
  if (m.startsWith('withholding_type')) return 'Retención sin cuenta asignada';
  return m;
};

const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return '-'; const p = String(iso).slice(0, 10).split('-'); return p.length === 3 ? p.reverse().join('/') : iso; };

const EMPTY_FILTERS = {
  date_from: '', date_to: '', source_type: 'all', q_text: '',
  account_code: 'all', amount_min: '', amount_max: '', status: 'all',
};
const FILTERS_KEY = 'journal_filters_v1';
const loadSavedFilters = () => {
  try { return { ...EMPTY_FILTERS, ...JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}') }; }
  catch { return { ...EMPTY_FILTERS }; }
};

export default function AccountingJournal() {
  const { user } = useOutletContext();
  const [entries, setEntries] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(loadSavedFilters);
  const [searchText, setSearchText] = useState(() => loadSavedFilters().q_text);
  const [accounts, setAccounts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reposting, setReposting] = useState(false);
  const [linkedEntry, setLinkedEntry] = useState(null);
  const [auditEntryId, setAuditEntryId] = useState(null);
  const [highlighted, setHighlighted] = useState(null);

  const canCreate = userCan(user, 'contabilidad', 'crear');
  const canRepost = userCan(user, 'contabilidad', 'editar');

  useEffect(() => { localStorage.setItem(FILTERS_KEY, JSON.stringify(filters)); }, [filters]);
  useEffect(() => {
    const t = setTimeout(() => setFilters(f => (f.q_text === searchText ? f : { ...f, q_text: searchText })), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const queryParams = useCallback(() => {
    const p = {};
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.source_type !== 'all') p.source_type = filters.source_type;
    if (filters.q_text?.trim()) p.q_text = filters.q_text.trim();
    if (filters.account_code !== 'all') p.account_code = filters.account_code;
    if (filters.amount_min !== '' && filters.amount_min !== null) p.amount_min = filters.amount_min;
    if (filters.amount_max !== '' && filters.amount_max !== null) p.amount_max = filters.amount_max;
    if (filters.status !== 'all') p.status = filters.status;
    return p;
  }, [filters]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/accounting/entries`, { params: queryParams(), withCredentials: true });
      setEntries(r.data);
    } catch { toast.error('Error al cargar el Libro Diario'); } finally { setLoading(false); }
  }, [queryParams]);

  const goToEntry = async (entryNumber) => {
    if (!entryNumber) return;
    const el = document.getElementById(`journal-entry-${entryNumber}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlighted(entryNumber);
      setTimeout(() => setHighlighted(h => (h === entryNumber ? null : h)), 2500);
      return;
    }
    try {
      const r = await axios.get(`${BACKEND_URL}/api/accounting/entries/by-number/${entryNumber}`, { withCredentials: true });
      setLinkedEntry(r.data);
    } catch { toast.error(`No se encontró el asiento #${entryNumber}`); }
  };


  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coveragePosting, setCoveragePosting] = useState(false);

  const fetchPending = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/accounting/pending`, { withCredentials: true });
      setPending(r.data);
    } catch { /* silencioso */ }
  }, []);

  const fetchCoverage = useCallback(async () => {
    setCoverageLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const r = await axios.get(`${BACKEND_URL}/api/accounting/coverage-audit`, { params, withCredentials: true });
      setCoverage(r.data);
    } catch { toast.error('Error al ejecutar la auditoría de cobertura'); }
    finally { setCoverageLoading(false); }
  }, [filters.date_from, filters.date_to]);

  const handleCoveragePost = async () => {
    setCoveragePosting(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const r = await axios.post(`${BACKEND_URL}/api/accounting/coverage-audit/post`, {}, { params, withCredentials: true });
      const failed = (r.data.failed || []).length;
      toast.success(`Asientos generados: ${r.data.posted}${failed ? ` · ${failed} no se pudieron generar` : ''}`);
      if (failed) setCoverage(prev => ({ ...prev, failed: r.data.failed }));
      fetchCoverage(); fetchPending(); fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al generar los asientos faltantes'); }
    finally { setCoveragePosting(false); }
  };

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { fetchPending(); }, [fetchPending]);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/accounting/accounts?only_postable=true`, { withCredentials: true })
      .then(r => setAccounts(r.data)).catch(() => {});
  }, []);

  const buildUrl = (path) => {
    const url = new URL(`${BACKEND_URL}${path}`);
    Object.entries(queryParams()).forEach(([k, v]) => url.searchParams.set(k, v));
    return url.toString();
  };

  const handleRepost = async () => {
    setReposting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/accounting/repost-pending`, {}, { withCredentials: true });
      toast.success(`Asentados: ${r.data.posted} · Aún pendientes: ${r.data.still_pending}`);
      fetchPending(); fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al reprocesar'); } finally { setReposting(false); }
  };

  const totalDebit = entries.reduce((s, e) => s + (e.total_debit || 0), 0);
  const totalCredit = entries.reduce((s, e) => s + (e.total_credit || 0), 0);

  return (
    <div className="space-y-6" data-testid="accounting-journal-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold">Libro Diario</h1>
            <p className="text-sm text-muted-foreground">Asientos contables de partida doble</p>
          </div>
        </div>
        {canCreate && <Button onClick={() => setDialogOpen(true)} data-testid="new-manual-entry-btn"><Plus className="w-4 h-4 mr-2" />Asiento manual</Button>}
      </div>

      <Tabs defaultValue="diario">
        <TabsList>
          <TabsTrigger value="diario" data-testid="tab-diario">Asientos</TabsTrigger>
          <TabsTrigger value="pendientes" data-testid="tab-pendientes">
            Pendientes de asentar {pending.length > 0 && <Badge className="ml-2 bg-amber-500">{pending.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="diario" className="space-y-4 mt-4">
          <Card><CardContent className="pt-6">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[280px] flex-1">
                <Label className="text-xs">Buscar</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-3 text-muted-foreground" />
                  <Input className="pl-8" value={searchText} onChange={e => setSearchText(e.target.value)}
                    placeholder="N° de asiento, comprobante, concepto o proveedor/residente…"
                    data-testid="filter-q-text" />
                </div>
              </div>
              <div><Label className="text-xs">Desde</Label><Input type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-date-from" /></div>
              <div><Label className="text-xs">Hasta</Label><Input type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-date-to" /></div>
              <div className="min-w-48">
                <Label className="text-xs">Origen</Label>
                <Select value={filters.source_type} onValueChange={v => setFilters({ ...filters, source_type: v })}>
                  <SelectTrigger data-testid="filter-source-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {Object.entries(SOURCE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-3 mt-3">
              <div className="min-w-[320px]">
                <Label className="text-xs">Cuenta contable</Label>
                <SearchableSelect
                  value={filters.account_code}
                  onChange={v => setFilters({ ...filters, account_code: v })}
                  options={[{ value: 'all', label: 'Todas las cuentas' },
                    ...accounts.map(a => ({ value: a.code, label: `${a.code} · ${a.name}`, hint: a.name }))]}
                  placeholder="Buscar cuenta por código o nombre…"
                  testId="filter-account-code"
                />
              </div>
              <div className="w-36"><Label className="text-xs">Importe desde</Label>
                <MoneyInput value={filters.amount_min} onChange={e => setFilters({ ...filters, amount_min: e.target.value })} data-testid="filter-amount-min" /></div>
              <div className="w-36"><Label className="text-xs">Importe hasta</Label>
                <MoneyInput value={filters.amount_max} onChange={e => setFilters({ ...filters, amount_max: e.target.value })} data-testid="filter-amount-max" /></div>
              <div className="min-w-44">
                <Label className="text-xs">Estado</Label>
                <Select value={filters.status} onValueChange={v => setFilters({ ...filters, status: v })}>
                  <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="vigentes">Vigentes</SelectItem>
                    <SelectItem value="reversados">Reversados</SelectItem>
                    <SelectItem value="reversos">Reversos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 ml-auto">
                <Button variant="ghost" onClick={() => { setFilters({ ...EMPTY_FILTERS }); setSearchText(''); }} data-testid="clear-filters-btn">
                  <X className="w-4 h-4 mr-2" />Limpiar filtros
                </Button>
                <Button variant="outline" onClick={() => window.open(buildUrl('/api/accounting/entries.pdf'), '_blank')} data-testid="export-journal-pdf"><FileText className="w-4 h-4 mr-2" />PDF</Button>
                <Button variant="outline" onClick={() => window.open(buildUrl('/api/accounting/entries.xlsx'), '_blank')} data-testid="export-journal-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              {loading ? 'Buscando…' : `${entries.length} asiento(s) encontrados`} · El importe busca por total del asiento o por cualquier línea que coincida. Los exportes usan estos mismos filtros.
            </p>
          </CardContent></Card>

          <Card><CardContent className="pt-6">
            {loading ? <p className="text-center py-8 text-muted-foreground">Cargando…</p> : entries.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground" data-testid="journal-empty">No hay asientos en el período seleccionado.</p>
            ) : (
              <div className="space-y-5" data-testid="journal-entries">
                {entries.map(e => (
                  <div key={e.entry_id} id={`journal-entry-${e.entry_number}`}
                    className={`border rounded-lg overflow-hidden transition-shadow ${highlighted === e.entry_number ? 'ring-2 ring-amber-400 shadow-lg' : ''}`}
                    data-testid={`journal-entry-${e.entry_number}`}>
                    <div className="bg-muted/50 px-4 py-2 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold">#{e.entry_number}</span>
                          <span className="text-muted-foreground">{fmtDate(e.date)}</span>
                          <Badge variant="outline">{SOURCE_LABELS[e.source_type] || e.source_type}</Badge>
                          {e.is_reversed && (
                            <button type="button" onClick={() => goToEntry(e.reversed_by_entry_number)}
                              data-testid={`link-reversed-by-${e.entry_number}`}
                              className="inline-flex items-center gap-1 rounded-full bg-rose-500 px-2 py-0.5 text-xs font-medium text-white hover:bg-rose-600 transition-colors">
                              <Link2 className="w-3 h-3" />
                              {e.reversed_by_entry_number ? `Reversado por #${e.reversed_by_entry_number}` : 'Reversado'}
                            </button>
                          )}
                          {e.reverses_entry_id && (
                            <button type="button" onClick={() => goToEntry(e.reverses_entry_number)}
                              data-testid={`link-reverses-${e.entry_number}`}
                              className="inline-flex items-center gap-1 rounded-full bg-slate-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-slate-700 transition-colors">
                              <Link2 className="w-3 h-3" />
                              {e.reverses_entry_number ? `Reverso del #${e.reverses_entry_number}` : 'Reverso'}
                            </button>
                          )}
                        </div>
                        <span className="font-medium text-right">{e.description}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <EntryAuditButton entryId={e.entry_id} onOpen={setAuditEntryId} testId={`entry-audit-btn-${e.entry_number}`} />
                        <span data-testid={`entry-created-line-${e.entry_number}`}>
                          {e.source_type === 'manual' ? 'Asiento manual' : 'Generado automáticamente'} · {e.created_by_name || 'Sistema'}
                          {e.created_at ? ` · ${fmtDateTime(e.created_at)}` : ''}
                        </span>
                      </div>
                    </div>
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead className="w-32">Código</TableHead><TableHead>Cuenta</TableHead>
                        <TableHead className="text-right w-32">Debe</TableHead><TableHead className="text-right w-32">Haber</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {e.lines.map((ln, i) => {
                          const match = filters.account_code !== 'all' && ln.account_code === filters.account_code;
                          return (
                            <TableRow key={i} className={match ? 'bg-amber-50 font-medium' : ''}>
                              <TableCell className="font-mono text-xs" style={ln.debit > 0 ? {} : { paddingLeft: 28 }}>{ln.account_code}</TableCell>
                              <TableCell style={ln.debit > 0 ? {} : { paddingLeft: 28 }}>{ln.account_name}</TableCell>
                              <TableCell className="text-right">{ln.debit > 0 ? fmtMoney(ln.debit) : ''}</TableCell>
                              <TableCell className="text-right">{ln.credit > 0 ? fmtMoney(ln.credit) : ''}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ))}
                <div className="flex justify-end gap-8 border-t-2 border-foreground pt-3 font-bold">
                  <span>Total Debe: {fmtMoney(totalDebit)}</span>
                  <span>Total Haber: {fmtMoney(totalCredit)}</span>
                </div>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="pendientes" className="space-y-4 mt-4">
          <Card><CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                Documentos de negocio que no pudieron generar asiento (falta de mapeos en el Plan de Cuentas o algún error al generarlo).
              </p>
              {canRepost && pending.length > 0 && (
                <Button onClick={handleRepost} disabled={reposting} data-testid="repost-pending-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${reposting ? 'animate-spin' : ''}`} />Reprocesar pendientes
                </Button>
              )}
            </div>
            {pending.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground" data-testid="pending-empty">✔ No hay documentos pendientes. Todo está asentado.</p>
            ) : (
              <Table data-testid="pending-table">
                <TableHeader><TableRow>
                  <TableHead>Origen</TableHead><TableHead>Documento</TableHead><TableHead>Cuentas a configurar</TableHead><TableHead>Último intento</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {pending.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell><Badge variant="outline">{SOURCE_LABELS[p.source_type] || p.source_type}</Badge></TableCell>
                      <TableCell>
                        <span className="font-medium">{p.reference || '-'}</span>
                        {p.description && <span className="block text-xs text-muted-foreground">{p.description}</span>}
                      </TableCell>
                      <TableCell><div className="flex flex-wrap gap-1">{(p.missing || []).map((m, j) => <Badge key={j} className="bg-amber-100 text-amber-800">{missingLabel(m)}</Badge>)}</div></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.last_attempt_at ? fmtDateTime(p.last_attempt_at) : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>

          <Card data-testid="coverage-audit-card"><CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4 gap-4">
              <div>
                <p className="text-sm font-semibold">Auditoría de cobertura contable</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Compara los documentos emitidos (facturas, notas de crédito, cobranzas, órdenes de pago,
                  comprobantes de compra, recibos de obras sociales, ajustes y transferencias) contra el
                  Libro Diario y detecta los que <b>no tienen asiento</b>, incluso si nunca quedaron en la
                  lista de pendientes. Respeta el rango de fechas de los filtros de arriba.
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" onClick={fetchCoverage} disabled={coverageLoading} data-testid="coverage-run-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${coverageLoading ? 'animate-spin' : ''}`} />
                  {coverage ? 'Volver a revisar' : 'Ejecutar auditoría'}
                </Button>
                {canRepost && coverage?.summary?.count > 0 && (
                  <Button onClick={handleCoveragePost} disabled={coveragePosting} data-testid="coverage-post-btn">
                    {coveragePosting ? 'Generando...' : 'Generar asientos faltantes'}
                  </Button>
                )}
              </div>
            </div>

            {!coverage ? (
              <p className="text-center py-6 text-sm text-muted-foreground" data-testid="coverage-idle">
                Ejecutá la auditoría para revisar la cobertura del Libro Diario.
              </p>
            ) : coverage.summary.count === 0 ? (
              <p className="text-center py-6 text-muted-foreground" data-testid="coverage-empty">
                ✔ Todos los documentos del período tienen su asiento contable.
              </p>
            ) : (
              <div className="space-y-3" data-testid="coverage-results">
                <div className="rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-900 p-3 text-sm text-rose-900 dark:text-rose-200">
                  <b>{coverage.summary.count}</b> documento(s) sin asiento por{' '}
                  <b>{fmtMoney(coverage.summary.amount)}</b>.
                  <span className="block text-xs mt-1">
                    {Object.entries(coverage.summary.by_module).map(([m, v]) => `${m}: ${v.count}`).join(' · ')}
                  </span>
                </div>
                <Table data-testid="coverage-table">
                  <TableHeader><TableRow>
                    <TableHead>Módulo</TableHead><TableHead>Documento</TableHead>
                    <TableHead>Fecha</TableHead><TableHead className="text-right">Importe</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {coverage.rows.map((r, i) => (
                      <TableRow key={i} data-testid={`coverage-row-${r.source_id}`}>
                        <TableCell><Badge variant="outline">{r.module}</Badge></TableCell>
                        <TableCell>
                          <span className="font-medium">{r.reference}</span>
                          {r.description && <span className="block text-xs text-muted-foreground">{r.description}</span>}
                        </TableCell>
                        <TableCell className="text-xs">{r.date ? fmtDate(r.date) : '-'}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtMoney(r.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {coverage.failed?.length > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-900 dark:text-amber-200" data-testid="coverage-failed">
                    <b>No se pudieron generar:</b>
                    <ul className="mt-1 space-y-0.5">
                      {coverage.failed.map((f, i) => <li key={i}>{f.reference}: {f.error}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      <ManualEntryDialog open={dialogOpen} onClose={() => setDialogOpen(false)} accounts={accounts} onSaved={() => { setDialogOpen(false); fetchEntries(); }} />
      <LinkedEntryDialog entry={linkedEntry} onClose={() => setLinkedEntry(null)} />
      <JournalEntryAuditDialog entryId={auditEntryId} onClose={() => setAuditEntryId(null)} />
    </div>
  );
}

function LinkedEntryDialog({ entry, onClose }) {
  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="linked-entry-dialog">
        <DialogHeader>
          <DialogTitle>Asiento #{entry?.entry_number}</DialogTitle>
          <DialogDescription>
            {fmtDate(entry?.date)} · {entry?.description}
            <span className="block mt-1 text-xs">
              Este asiento quedó fuera de los filtros actuales, así que se muestra acá para no perder el vínculo.
            </span>
          </DialogDescription>
        </DialogHeader>
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-32">Código</TableHead><TableHead>Cuenta</TableHead>
            <TableHead className="text-right w-32">Debe</TableHead><TableHead className="text-right w-32">Haber</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(entry?.lines || []).map((ln, i) => (
              <TableRow key={i}>
                <TableCell className="font-mono text-xs">{ln.account_code}</TableCell>
                <TableCell>{ln.account_name}</TableCell>
                <TableCell className="text-right">{ln.debit > 0 ? fmtMoney(ln.debit) : ''}</TableCell>
                <TableCell className="text-right">{ln.credit > 0 ? fmtMoney(ln.credit) : ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="linked-entry-close">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualEntryDialog({ open, onClose, accounts, onSaved }) {
  const today = todayLocalISO();
  const [form, setForm] = useState({ date: today, description: '' });
  const [lines, setLines] = useState([{ account_code: '', debit: '', credit: '' }, { account_code: '', debit: '', credit: '' }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setForm({ date: today, description: '' }); setLines([{ account_code: '', debit: '', credit: '' }, { account_code: '', debit: '', credit: '' }]); }
  }, [open]); // eslint-disable-line

  const setLine = (i, patch) => setLines(lines.map((l, j) => j === i ? { ...l, ...patch } : l));
  const addLine = () => setLines([...lines, { account_code: '', debit: '', credit: '' }]);
  const rmLine = (i) => setLines(lines.filter((_, j) => j !== i));

  const totD = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totC = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = Math.abs(totD - totC) < 0.005 && totD > 0;

  const save = async () => {
    if (!form.description.trim()) { toast.error('Ingresá un concepto'); return; }
    const payloadLines = lines.filter(l => l.account_code && ((parseFloat(l.debit) || 0) > 0 || (parseFloat(l.credit) || 0) > 0))
      .map(l => ({ account_code: l.account_code, debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0 }));
    if (payloadLines.length < 2) { toast.error('Cargá al menos 2 líneas con cuenta e importe'); return; }
    if (!balanced) { toast.error('El asiento no balancea (Debe ≠ Haber)'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/accounting/entries/manual`, { date: form.date, description: form.description, lines: payloadLines }, { withCredentials: true });
      toast.success('Asiento manual creado');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear asiento'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Nuevo asiento manual</DialogTitle><DialogDescription>Cargá al menos 2 líneas. El asiento debe balancear (Debe = Haber) para poder guardarse.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Fecha *</Label><Input type="date" max={todayLocalISO()} value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="manual-date" /></div>
            <div className="col-span-2"><Label>Concepto *</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Ej: Ajuste contable de cierre" data-testid="manual-description" /></div>
          </div>
          <div className="space-y-2">
            <Label>Líneas</Label>
            {lines.map((l, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Select value={l.account_code} onValueChange={v => setLine(i, { account_code: v })}>
                  <SelectTrigger className="flex-1" data-testid={`manual-line-account-${i}`}><SelectValue placeholder="Cuenta…" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {accounts.map(a => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <MoneyInput placeholder="Debe" className="w-32" value={l.debit} onChange={e => setLine(i, { debit: e.target.value, credit: '' })} data-testid={`manual-line-debit-${i}`} />
                <MoneyInput placeholder="Haber" className="w-32" value={l.credit} onChange={e => setLine(i, { credit: e.target.value, debit: '' })} data-testid={`manual-line-credit-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => rmLine(i)} disabled={lines.length <= 2}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addLine}><Plus className="w-4 h-4 mr-1" />Agregar línea</Button>
          </div>
          <div className={`flex justify-end gap-6 text-sm font-medium ${balanced ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="manual-balance-indicator">
            <span>Debe: {fmtMoney(totD)}</span><span>Haber: {fmtMoney(totC)}</span>
            <span>{balanced ? '✔ Balancea' : '⚠ No balancea'}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving || !balanced} data-testid="manual-save-btn">Guardar asiento</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
